import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { DiscountCampaignAnalyticsShape } from "../../types/discount-analytics-types";
import { CampaignRoiCard } from "./campaign-roi-card";
import { DiscountFunnel } from "./discount-funnel";
import { DiscountRedemptionTrendChart } from "./discount-redemption-trend-chart";
import { HaloServicesChart } from "./halo-services-chart";
import { SegmentBreakdownCard } from "./segment-breakdown-card";
import { SourceBreakdown } from "./source-breakdown";
import { TimingHeatmap } from "./timing-heatmap";

interface Props {
  campaign: DiscountCampaignAnalyticsShape;
}

function formatPercent(v: number | null | undefined): string {
  return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
}

export function DiscountCampaignDetail({ campaign }: Props) {
  const { t } = useTranslation("reports");

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h2 className="text-lg font-semibold">{campaign.discountName}</h2>

      <CampaignRoiCard campaign={campaign} />

      <div className="grid gap-3 lg:grid-cols-2">
        <DiscountFunnel funnel={campaign.funnel} conversionRates={campaign.funnelConversionRates} />
        <SourceBreakdown rows={campaign.sourceBreakdown} />
      </div>

      <SegmentBreakdownCard rows={campaign.segmentBreakdown} />

      <HaloServicesChart campaign={campaign} />

      <TimingHeatmap
        dayOfWeek={campaign.dayOfWeekDistribution}
        hourOfDay={campaign.hourOfDayDistribution}
      />

      <DiscountRedemptionTrendChart data={campaign.redemptionTrend} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t("discounts.detail.serviceBreakdown")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("discounts.detail.serviceName")}</TableHead>
                  <TableHead className="text-right">
                    {t("discounts.detail.redemptionCount")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("discounts.detail.amountDiscounted")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaign.serviceBreakdown.map((s) => (
                  <TableRow key={s.serviceId}>
                    <TableCell>{s.serviceName}</TableCell>
                    <TableCell className="text-right">{s.redemptionCount}</TableCell>
                    <TableCell className="text-right">
                      <PriceDisplay amount={s.amountDiscounted} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {campaign.publicCouponState ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  {t("discounts.detail.publicCoupon")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row
                  label={t("discounts.detail.couponCode")}
                  value={campaign.publicCouponState.code}
                />
                <Row
                  label={t("discounts.detail.usageCount")}
                  value={String(campaign.publicCouponState.usageCount)}
                />
                <Row
                  label={t("discounts.detail.maxUsages")}
                  value={
                    campaign.publicCouponState.maxUsages == null
                      ? "—"
                      : String(campaign.publicCouponState.maxUsages)
                  }
                />
                <Row
                  label={t("discounts.detail.exhaustionRate")}
                  value={formatPercent(campaign.publicCouponState.exhaustionRate)}
                />
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                {t("discounts.detail.wasted.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("discounts.detail.wasted.declined")}
                value={String(campaign.wasted.declinedCount)}
                extra={<PriceDisplay amount={campaign.wasted.declinedAmount} />}
              />
              <Row
                label={t("discounts.detail.wasted.noShow")}
                value={String(campaign.wasted.noShowCount)}
                extra={<PriceDisplay amount={campaign.wasted.noShowAmount} />}
              />
              <Row
                label={t("discounts.detail.wasted.rescheduled")}
                value={String(campaign.wasted.rescheduledCount)}
                extra={<PriceDisplay amount={campaign.wasted.rescheduledAmount} />}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                {t("discounts.detail.inFlight.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("discounts.detail.inFlight.count")}
                value={String(campaign.inFlightCount)}
              />
              <Row
                label={t("discounts.detail.inFlight.amount")}
                value=""
                extra={<PriceDisplay amount={campaign.inFlightAmount} />}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, extra }: { label: string; value: string; extra?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">
        {value}
        {extra ? <span className="ml-2 text-muted-foreground">{extra}</span> : null}
      </span>
    </div>
  );
}
